/* eslint-disable no-await-in-loop */
/* eslint-disable no-restricted-syntax */
/* eslint-disable no-useless-escape */
/* eslint-disable no-param-reassign */
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import lightStyles from './portalStylesLight.module.css';
import darkStyles from './portalStylesDark.module.css';

import AddDialogue from './AddDialogue';
import Notes from './Notes';

interface Props {
  vipList: any[];
  token: string;
  setVipList: React.SetStateAction<any>;
  orgId: string;
  lightMode: boolean;
}

const prefix = 'VIP-USER-';
const MAX_LEN = 100;
const MAX_PHONE_LEN = 20;

// Phone number validation regex - allows digits, *, ?, +, -, spaces, parentheses
const PHONE_REGEX = /^[\d\*\+\-\(\)\s\?]+$/;

export default function Vip({ vipList, token, setVipList, orgId, lightMode }: Props) {
  const [showAddDropdown, setShowAddDropdown] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const gridRef = useRef<AgGridReact>(null);
  const addButtonRef = useRef<HTMLButtonElement>(null);

  const styles = lightMode ? lightStyles : darkStyles;

  // Get selected rows from the grid
  const getSelectedRows = useCallback(() => {
    if (gridRef.current) {
      return gridRef.current.api.getSelectedRows();
    }
    return [];
  }, []);

  // Add popup
  const handleAddClick = useCallback(() => {
    setShowAddDropdown((prev) => !prev);
  }, []);

  // Add
  const handleAdd = useCallback(
    async (adds: { id: string; description: string }[]) => {
      try {
        setError(null);
        const addList: { PartitionKey: string; SortKey: string; name: string; number: string }[] = [];
        for (const add of adds) {
          const res = await (
            await fetch(`${process.env.REACT_APP_API_URL}/vip`, {
              method: 'POST',
              body: JSON.stringify({ name: add.description, number: add.id }),
              headers: {
                authorization: `Bearer ${token}`,
              },
            })
          ).json();
          addList.push({
            PartitionKey: `ORGANIZATION-${orgId}`,
            SortKey: `${prefix}${res.id}`,
            name: add.description,
            number: add.id,
          });
        }
        setVipList((oldVipList: any) => [...oldVipList, ...addList]);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to add VIP entry');
        console.error('Error adding VIP entry:', err);
      }
    },
    [token, orgId, setVipList],
  );

  // Delete
  const handleDelete = useCallback(async () => {
    try {
      setError(null);
      const selectedRows = getSelectedRows();
      if (selectedRows.length === 0) {
        setError('No rows selected for deletion');
        return;
      }
      const delIds: string[] = [];
      for (const row of selectedRows) {
        await fetch(`${process.env.REACT_APP_API_URL}/vip/${row.SortKey.split(prefix)[1]}`, {
          method: 'DELETE',
          headers: {
            authorization: `Bearer ${token}`,
          },
        });
        delIds.push(row.SortKey);
      }
      setVipList((oldVipList: any[]) => oldVipList.filter((vip: any) => !delIds.includes(vip.SortKey)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete VIP entry');
      console.error('Error deleting VIP entry:', err);
    }
  }, [getSelectedRows, token, setVipList]);

  // Edit value
  const onCellValueChanged = useCallback(
    async (event: any) => {
      try {
        setError(null);
        await fetch(`${process.env.REACT_APP_API_URL}/vip/${event.data.SortKey.split(prefix)[1]}`, {
          method: 'PUT',
          body: JSON.stringify({
            name: event.data.name,
            number: event.data.number,
          }),
          headers: {
            authorization: `Bearer ${token}`,
          },
        });
        setVipList((oldVipList: any[]) =>
          oldVipList.map((item) =>
            item.SortKey === event.data.SortKey ? { ...item, name: event.data.name, number: event.data.number } : item,
          ),
        );
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update VIP entry');
        console.error('Error updating VIP entry:', err);
      }
    },
    [token, setVipList],
  );

  const columnDefs = useMemo(
    () => [
      {
        headerName: '',
        field: 'selected',
        headerCheckboxSelection: true,
        checkboxSelection: true,
        suppressMenu: true,
        width: 50,
        suppressSizeToFit: true,
      },
      {
        headerName: 'Customer Number',
        field: 'number',
        editable: true,
        flex: 1,
        valueSetter: (params: any) => {
          const v = (params.newValue ?? "").toString().trim();
          if (v.length > MAX_PHONE_LEN || v.length < 1) {
            return false;
          }
          if (!PHONE_REGEX.test(v)) {
            return false;
          }
          params.data.number = v;
          return true;
        },
      },
      {
        headerName: 'Customer Name',
        field: 'name',
        editable: true,
        flex: 1,
        valueSetter: (params: any) => {
          const v = (params.newValue ?? "").toString().trim();
          if (v.length > MAX_LEN || v.length < 1) {
            return false;
          }
          params.data.name = v;
          return true;
        },
      },
    ],
    [],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: false,
      filter: true,
      resizable: true,
      minWidth: 50,
    }),
    [],
  );

  return (
    <div className={styles.gridContainer}>
      {error && (
        <div className={styles.errorMessage} style={{ 
          color: 'red', 
          padding: '8px', 
          margin: '8px 0', 
          backgroundColor: 'rgba(255, 0, 0, 0.1)', 
          borderRadius: '4px',
          border: '1px solid red'
        }}>
          {error}
        </div>
      )}
      <div className={styles.navbar}>
        <div className={styles.tabButtons}>
          <button type="button" className={`${styles.subTabButton} ${styles.subTabButtonActive}`}>
            Vip Configurations
          </button>
        </div>
        <div className={styles.actionButtonsContainer}>
          <div className={styles.actionButtonsWrapper}>
            <button ref={addButtonRef} type="button" onClick={handleAddClick} className={styles.actionButton}>
              Add
            </button>
            {showAddDropdown && (
              <AddDialogue
                lightMode={lightMode}
                onAdd={handleAdd}
                onClose={() => setShowAddDropdown(false)}
                format='"&lt;Number&gt; &lt;Name&gt;"'
                example='"*8005550199* Ryan & Associates"'
                params={2}
                validate={(codes) => {
                  for (const code of codes) {
                    if (code.id.length > MAX_PHONE_LEN) {
                      return `Phone number "${code.id}" is too long (must be ${MAX_PHONE_LEN} characters or less).`;
                    }
                    if (code.id.length < 1) {
                      return `Phone number cannot be empty.`;
                    }
                    if (!PHONE_REGEX.test(code.id)) {
                      return `Phone number "${code.id}" contains invalid characters. Only digits, *, ?, +, -, spaces, and parentheses are allowed.`;
                    }
                    if (code.description.length > MAX_LEN) {
                      return `Customer name "${code.description}" is too long (must be ${MAX_LEN} characters or less).`;
                    }
                    if (code.description.length < 1) {
                      return `Customer name cannot be empty.`;
                    }
                  }
                  return null;
                }}
              />
            )}
            <button type="button" onClick={handleDelete} className={styles.actionButton}>
              Delete
            </button>
          </div>
        </div>
      </div>

      <div
        className={`ag-theme-alpine${!lightMode ? '-dark' : ''}  ${styles.agThemeAlpineOverrides} ${
          styles.gridWrapper
        }`}
        style={{ flex: 3 }}
      >
        <AgGridReact
          ref={gridRef}
          rowData={vipList}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          onCellValueChanged={onCellValueChanged}
          singleClickEdit
          stopEditingWhenCellsLoseFocus
          animateRows
          suppressCellFocus
          rowSelection="multiple"
          suppressRowClickSelection
          suppressDragLeaveHidesColumns
          headerHeight={40}
          rowHeight={40}
        />
      </div>
      <Notes
        lightMode={lightMode}
        notes={`
            This page defines the VIP number to name bindings. We allow optional regex inclusions,
            The '?' symbol matches zero or one occurrence of the preceding character.
            The '*' symbol Matches zero or more occurrences of the preceding character.
            '?' Example: colou?r Matches both: color and colour
            '*' Example: go*gle Matches: ggle, gogle, google, gooogle, etc.
            
            Phone numbers can contain digits, *, ?, +, -, spaces, and parentheses.
            Customer names must be between 1 and ${MAX_LEN} characters.
          `}
        // documents={[
        //   { name: 'Supporting Document', uri: 'https://google.ca' },
        //   { name: 'Instruction Manual', uri: 'https://yahoo.ca' },
        //   { name: 'Demo', uri: 'https://bing.com' },
        // ]}
      />
    </div>
  );
}
